import { PageId } from "../common/pageId"
import { PageTitle } from "../common/pageText"
import { FieldName, FragmentFieldName } from "../common/text"
import { ActionType } from "../common/transaction"
import { CodeType } from "../components/AirportSelector"

export interface IExcludeFieldConfig {
  [key: string]: boolean
}

export type IFieldConfig = {
  [key in FieldName]?: boolean
}

export type IFragmentFieldConfig = {
  [key in FragmentFieldName]?: boolean
}

export interface IAirportseasonSelectorConfig {
  selectCodeType?: boolean
  dropdownMultiSelect: boolean
}

export interface IPageConfig {
  selectCodeType?: boolean
  dropdownMultiSelect?: boolean
  codeType: CodeType
  pageTitle?: string
  flightSelectorConfig?: IFieldConfig
  fragmentConfig?: IFragmentFieldConfig
  actionConfig?: ActionType[]
  airportseasonSelectorConfig?: IAirportseasonSelectorConfig
}

export interface IConfig {
  [key: string]: IPageConfig
}

const excludeGaba: IFieldConfig = {
  normalizePeriod: true,
  prevNext: true,
  useOwnOperators: true,
  viewSubmittedShl: true,
  canxSinceDate: true,
  towbar: true,
  handler: true,
  flightRule: true
}
const excludeHandling: IFieldConfig = {
  normalizePeriod: true,
  prevNext: true,
  useOwnOperators: true,
  viewSubmittedShl: true,
  canxSinceDate: true,
  clearCode: true,
  excludeNoslots: true,
  noslotsOnly: true,
  parkingLocation: true
}
const exclude: IFieldConfig = {
  aircraftRegistration: true,
  useOwnOperators: true,
  viewSubmittedShl: true,
  slotId: true,
  canxSinceDate: true,
  towbar: true,
  handler: true,
  flightRule: true,
  parkingLocation: true
}
const excludeSHL: IFieldConfig = {
  clearCode: true,
  aircraftRegistration: true,
  useOwnOperators: true,
  delink: true,
  normalizePeriod: true,
  slotId: true,
  canxSinceDate: true,
  towbar: true,
  handler: true,
  flightRule: true,
  parkingLocation: true
}
const excludeEditOffers: IFieldConfig = {
  clearCode: true,
  aircraftRegistration: true,
  useOwnOperators: true,
  delink: true,
  waitlist: true,
  excludeNoslots: true,
  noslotsOnly: true,
  viewSubmittedShl: true,
  slotId: true,
  canxSinceDate: true,
  towbar: true,
  handler: true,
  flightRule: true,
  parkingLocation: true
}
const excludeEUACA: IFieldConfig = {
  clearCode: true,
  lastUpdate: true,
  delink: true,
  normalizePeriod: true,
  terminal: true,
  viewSubmittedShl: true,
  slotId: true,
  canxSinceDate: true,
  towbar: true,
  handler: true,
  flightRule: true,
  parkingLocation: true
}

const excludeSlotMonitor: IFieldConfig = {
  aircraftRegistration: true,
  delink: true,
  normalizePeriod: true,
  doop: true,
  clearCode: true,
  clearedTimeFrom: true,
  clearedTimeTo: true,
  requiredTimeTo: true,
  requiredTimeFrom: true,
  waitlist: true,
  excludeNoslots: true,
  noslotsOnly: true,
  prevNext: true,
  startDate: true,
  endDate: true,
  useOwnOperators: true,
  excludeOper: true,
  serviceTypes: true,
  terminal: true,
  aircraftType: true,
  origDest: true,
  lastUpdate: true,
  canxSinceDate: true,
  serviceNumberTo: true,
  viewSubmittedShl: true,
  slotId: true,
  towbar: true,
  handler: true,
  flightRule: true,
  parkingLocation: true
}

const excludeCanceled: IFieldConfig = {
  aircraftRegistration: true,
  delink: true,
  normalizePeriod: true,
  doop: true,
  clearCode: true,
  clearedTimeFrom: true,
  clearedTimeTo: true,
  requiredTimeTo: true,
  requiredTimeFrom: true,
  waitlist: true,
  excludeNoslots: true,
  noslotsOnly: true,
  serviceNumber: true,
  serviceNumberTo: true,
  prevNext: true,
  lastUpdate: true,
  serviceTypes: true,
  terminal: true,
  aircraftType: true,
  flightMode: true,
  origDest: true,
  viewSubmittedShl: true,
  towbar: true,
  handler: true,
  flightRule: true,
  excludeOper: true,
  slotId: true,
  parkingLocation: true
}
const excludeOscar: IFieldConfig = {
  aircraftRegistration: true,
  serviceNumber: true,
  clearCode: true,
  clearedTimeFrom: true,
  clearedTimeTo: true,
  requiredTimeTo: true,
  requiredTimeFrom: true,
  waitlist: true,
  excludeNoslots: true,
  noslotsOnly: true,
  startDate: true,
  endDate: true,
  doop: true,
  flightMode: true,
  normalizePeriod: true,
  delink: true,
  origDest: true,
  prevNext: true,
  aircraftType: true,
  terminal: true,
  serviceTypes: true,
  lastUpdate: true,
  viewSubmittedShl: true,
  slotId: true,
  canxSinceDate: true,
  towbar: true,
  handler: true,
  flightRule: true,
  parkingLocation: true
}

const excludeSD: IFieldConfig = {
  doop: true,
  endDate: true,
  useOwnOperators: true,
  viewSubmittedShl: true,
  slotId: true,
  canxSinceDate: true,
  towbar: true,
  handler: true,
  flightRule: true,
  parkingLocation: true
}
const excludeBreakout: IFragmentFieldConfig = {
  startDate: true,
  endDate: true,
  doop: true,
  separationOption: true
}

const {
  viewFlights,
  viewGaBaFlights,
  addGaBaFlights,
  editGaBaFlights,
  editFlightSeries,
  editSingleDateFlights,
  addFlightSeries,
  addSubmissionFlights,
  addSingleDateFlights,
  downloadFlights,
  reportFlights,
  downloadCancelled,
  downloadCancelledHandling,
  oscar,
  editShlFlights,
  EditOffers,
  viewBaselineFlights,
  viewEuacaFlights,
  slotMonitoring,
  addHandling,
  viewHandling,
  editParking,
  disableMessageProcessing
} = PageTitle

export const getPageConfig: IConfig = {
  [PageId.SlotMonitor]: {
    codeType: CodeType.IATA,
    pageTitle: slotMonitoring,
    flightSelectorConfig: excludeSlotMonitor,
    airportseasonSelectorConfig: {
      dropdownMultiSelect: false
    }
  },
  [PageId.ViewFlightsBaseline]: {
    codeType: CodeType.IATA,
    pageTitle: viewBaselineFlights,
    flightSelectorConfig: {
      viewSubmittedShl: true,
      slotId: true,
      canxSinceDate: true,
      towbar: true,
      handler: true,
      flightRule: true,
      parkingLocation: true
    },
    airportseasonSelectorConfig: {
      dropdownMultiSelect: true
    }
  },
  [PageId.ViewFlightsEUACA]: {
    codeType: CodeType.IATA,
    pageTitle: viewEuacaFlights,
    flightSelectorConfig: excludeEUACA,
    airportseasonSelectorConfig: {
      dropdownMultiSelect: true
    }
  },
  [PageId.ViewFlightsGABA]: {
    codeType: CodeType.ICAO,
    pageTitle: viewGaBaFlights,
    flightSelectorConfig: excludeGaba,
    airportseasonSelectorConfig: {
      dropdownMultiSelect: true
    }
  },
  [PageId.AddFlightsSeries]: {
    codeType: CodeType.IATA,
    pageTitle: addFlightSeries
  },
  [PageId.AddSHL]: {
    codeType: CodeType.IATA,
    pageTitle: addSubmissionFlights
  },
  [PageId.AddFlightsGABA]: {
    codeType: CodeType.ICAO,
    pageTitle: addGaBaFlights
  },
  [PageId.EditFlightsGABA]: {
    codeType: CodeType.ICAO,
    pageTitle: editGaBaFlights,
    flightSelectorConfig: excludeGaba,
    fragmentConfig: excludeBreakout,
    airportseasonSelectorConfig: {
      dropdownMultiSelect: true
    },
    actionConfig: [
      ActionType.SHLCHANGENOOFFER,
      ActionType.SHLCHANGECONTINUATION,
      ActionType.MERGE,
      ActionType.SWAPPERIOD,
      ActionType.SHLHISTORICS,
      ActionType.ACCEPT,
      ActionType.PEND,
      ActionType.DECLINE
    ]
  },
  [PageId.ViewFlights]: {
    pageTitle: viewFlights,
    codeType: CodeType.IATA,
    flightSelectorConfig: {
      viewSubmittedShl: true,
      slotId: true,
      canxSinceDate: true,
      towbar: true,
      handler: true,
      flightRule: true,
      parkingLocation: true
    },
    airportseasonSelectorConfig: {
      selectCodeType: false,
      dropdownMultiSelect: true
    }
  },
  [PageId.EditFlightsSeries]: {
    codeType: CodeType.IATA,
    pageTitle: editFlightSeries,
    flightSelectorConfig: exclude,
    airportseasonSelectorConfig: {
      selectCodeType: false,
      dropdownMultiSelect: true
    },
    actionConfig: [
      ActionType.SHLCHANGENOOFFER,
      ActionType.SHLCHANGECONTINUATION,
      ActionType.SHLHISTORICS,
      ActionType.ACCEPT,
      ActionType.PEND,
      ActionType.DECLINE
    ]
  },
  [PageId.EditFlightsSD]: {
    codeType: CodeType.IATA,
    pageTitle: editSingleDateFlights,
    actionConfig: [
      ActionType.SHLCHANGENOOFFER,
      ActionType.SHLCHANGECONTINUATION,
      ActionType.MERGE,
      ActionType.SWAPPERIOD,
      ActionType.SHLHISTORICS,
      ActionType.ACCEPT,
      ActionType.PEND,
      ActionType.DECLINE
    ],
    flightSelectorConfig: excludeSD,
    fragmentConfig: excludeBreakout,
    airportseasonSelectorConfig: {
      selectCodeType: false,
      dropdownMultiSelect: true
    }
  },
  [PageId.EditSHL]: {
    codeType: CodeType.IATA,
    pageTitle: editShlFlights,
    flightSelectorConfig: excludeSHL,
    airportseasonSelectorConfig: {
      selectCodeType: false,
      dropdownMultiSelect: true
    },
    actionConfig: [
      ActionType.SEARCH,
      ActionType.SWAP,
      ActionType.SWAPPERIOD,
      ActionType.ACCEPT,
      ActionType.PEND,
      ActionType.DECLINE
    ]
  },
  [PageId.EditOffers]: {
    codeType: CodeType.IATA,
    pageTitle: EditOffers,
    flightSelectorConfig: excludeEditOffers,
    airportseasonSelectorConfig: {
      selectCodeType: false,
      dropdownMultiSelect: true
    },
    actionConfig: [
      ActionType.CHANGE,
      ActionType.SHLCHANGENOOFFER,
      ActionType.SHLCHANGECONTINUATION,
      ActionType.DELETE,
      ActionType.SEARCH,
      ActionType.MERGE,
      ActionType.SWAPPERIOD,
      ActionType.SWAP,
      ActionType.SWAPAC,
      ActionType.SWAPLINK,
      ActionType.SWAPARRTODEP,
      ActionType.SWAPDEPTOARR,
      ActionType.SHLHISTORICS,
      ActionType.LINK,
      ActionType.DELINK,
      ActionType.CLEARREQ
    ]
  },
  [PageId.DownloadFlights]: {
    pageTitle: downloadFlights,
    codeType: CodeType.IATA,
    flightSelectorConfig: {
      viewSubmittedShl: true,
      slotId: true,
      canxSinceDate: true,
      towbar: true,
      handler: true,
      flightRule: true,
      parkingLocation: true
    },
    airportseasonSelectorConfig: {
      selectCodeType: false,
      dropdownMultiSelect: true
    }
  },
  [PageId.DownloadCancelled]: {
    pageTitle: downloadCancelled,
    codeType: CodeType.IATA,
    flightSelectorConfig: excludeCanceled,
    airportseasonSelectorConfig: {
      selectCodeType: false,
      dropdownMultiSelect: true
    }
  },
  [PageId.DownloadCancelledHandling]: {
    pageTitle: downloadCancelledHandling,
    codeType: CodeType.ICAO,
    flightSelectorConfig: excludeCanceled,
    airportseasonSelectorConfig: {
      selectCodeType: false,
      dropdownMultiSelect: true
    }
  },
  [PageId.ScheduleDownload]: {
    codeType: CodeType.IATA,
    pageTitle: oscar,
    flightSelectorConfig: excludeOscar,
    airportseasonSelectorConfig: {
      selectCodeType: false,
      dropdownMultiSelect: false
    }
  },
  [PageId.CountReport]: {
    pageTitle: reportFlights,
    codeType: CodeType.IATA,
    flightSelectorConfig: {
      viewSubmittedShl: true,
      slotId: true,
      canxSinceDate: true,
      towbar: true,
      handler: true,
      flightRule: true,
      parkingLocation: true
    },
    airportseasonSelectorConfig: {
      selectCodeType: false,
      dropdownMultiSelect: false
    }
  },

  [PageId.AddFlightsSD]: {
    codeType: CodeType.IATA,
    pageTitle: addSingleDateFlights
  },

  [PageId.ViewHandling]: {
    pageTitle: viewHandling,
    codeType: CodeType.ICAO,
    flightSelectorConfig: excludeHandling,
    airportseasonSelectorConfig: {
      selectCodeType: false,
      dropdownMultiSelect: true
    }
  },

  [PageId.AddHandling]: {
    codeType: CodeType.ICAO,
    pageTitle: addHandling
  },
  [PageId.EditHandling]: {
    codeType: CodeType.ICAO,
    pageTitle: editParking,
    flightSelectorConfig: excludeHandling,
    airportseasonSelectorConfig: {
      dropdownMultiSelect: true
    },
    actionConfig: [
      ActionType.SHLCHANGENOOFFER,
      ActionType.SHLCHANGECONTINUATION,
      ActionType.MERGE,
      ActionType.SWAPPERIOD,
      ActionType.SWAP,
      ActionType.SWAPLINK,
      ActionType.SWAPAC,
      ActionType.SWAPARRTODEP,
      ActionType.SWAPDEPTOARR,
      ActionType.SHLHISTORICS,
      ActionType.ACCEPT,
      ActionType.PEND,
      ActionType.DECLINE,
      ActionType.SEARCH,
      ActionType.CLEARREQ,
      ActionType.DELINK,
      ActionType.LINK
    ]
  },
  [PageId.DisableMP]: {
    codeType: CodeType.IATA,
    pageTitle: disableMessageProcessing
  }
}
